import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, Save, MapPin, Phone, Mail, Clock, Star, Package,
  FileText, User, CheckCircle, XCircle, AlertTriangle, Store,
  Edit3, Eye, Briefcase, DollarSign, Activity, Wallet, Plus, Minus,
  CreditCard, RefreshCw, ChevronDown, ChevronUp, Trash2, Camera, ExternalLink
} from 'lucide-react';
import apiService from '../../apiService';
import Img from '../../components/Img';
import AssignMenuPanel from '../../components/AssignMenuPanel';

const BUSINESS_TYPES = [
  { value: 'restaurant', label: '🍽️ Restaurant' },
  { value: 'supermarket', label: '🛒 Supermarché' },
  { value: 'pharmacy', label: '💊 Pharmacie' },
  { value: 'fuel', label: '⛽ Station essence' },
  { value: 'shop', label: '🏪 Boutique' },
  { value: 'bakery', label: '🥖 Boulangerie' },
  { value: 'butcher', label: '🥩 Boucherie' },
  { value: 'bar', label: '🍺 Bar' },
  { value: 'cafe', label: '☕ Café' },
  { value: 'hotel', label: '🏨 Hôtel' },
  { value: 'laundry', label: '👔 Pressing' },
  { value: 'beauty_salon', label: '💇 Salon de beauté' },
  { value: 'gym', label: '🏋️ Salle de sport' },
  { value: 'electronics', label: '📱 Électronique' },
  { value: 'clothing', label: '👗 Vêtements' },
  { value: 'bookstore', label: '📚 Librairie' },
  { value: 'hardware', label: '🔧 Quincaillerie' },
  { value: 'florist', label: '💐 Fleuriste' },
  { value: 'vehicle_rental', label: '🚗 Location véhicule' },
  { value: 'other', label: '📦 Autre' },
];

const STATUS_CONFIG = {
  approved: { label: 'Validé', color: '#10B981', bg: '#ECFDF5', icon: CheckCircle },
  pending: { label: 'En attente', color: '#ca8a04', bg: '#fef9c3', icon: Clock },
  rejected: { label: 'Refusé', color: '#dc2626', bg: '#fef2f2', icon: XCircle },
  suspended: { label: 'Suspendu', color: '#ea580c', bg: '#fff7ed', icon: AlertTriangle },
};

// ─── Styles ──────────────────────────────────────────────────
const styles = {
  container: { maxWidth: '1100px', margin: '0 auto' },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
    border: '1px solid #E9EBEF', borderRadius: '10px', background: 'white',
    color: '#5C636E', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600',
    marginBottom: '1.25rem', transition: 'all 0.15s',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '1rem' },
  avatar: {
    width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover',
    background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.5rem', fontWeight: '700', color: '#10B981', flexShrink: 0,
  },
  headerRight: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  coverBand: {
    position: 'relative', width: '100%', height: '200px', borderRadius: '16px', overflow: 'hidden',
    background: '#F3F4F6', marginBottom: '1rem', border: '1px solid #E5E7EB',
  },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  coverEmpty: {
    width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.5rem', color: '#6B7280', fontSize: '0.875rem', border: '2px dashed #D1D5DB', borderRadius: '16px', boxSizing: 'border-box',
  },
  coverBtn: {
    position: 'absolute', right: '12px', bottom: '12px', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
    padding: '0.4rem 0.75rem', borderRadius: '10px', background: 'rgba(255,255,255,0.95)', color: '#1E2229',
    fontSize: '0.75rem', fontWeight: '700', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  badge: (bg, color) => ({
    padding: '0.35rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem',
    fontWeight: '700', background: bg, color, display: 'inline-flex',
    alignItems: 'center', gap: '0.35rem',
  }),
  card: {
    background: 'white', borderRadius: '16px', padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: '1rem',
  },
  cardTitle: {
    fontSize: '1rem', fontWeight: '700', color: '#1E2229',
    marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
  },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' },
  field: { marginBottom: '0.75rem' },
  label: {
    fontSize: '0.75rem', fontWeight: '600', color: '#9AA0A8',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.35rem',
  },
  value: { fontSize: '0.95rem', color: '#1E2229', fontWeight: '500' },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: '10px',
    border: '1.5px solid #E9EBEF', fontSize: '0.9rem', color: '#1E2229',
    outline: 'none', background: '#FFFFFF', boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  select: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: '10px',
    border: '1.5px solid #E9EBEF', fontSize: '0.9rem', color: '#1E2229',
    outline: 'none', background: '#FFFFFF', boxSizing: 'border-box',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.75rem', borderRadius: '10px',
    border: '1.5px solid #E9EBEF', fontSize: '0.9rem', color: '#1E2229',
    outline: 'none', background: '#FFFFFF', boxSizing: 'border-box',
    resize: 'vertical', minHeight: '80px',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none',
    background: '#FFB900', color: 'white', fontWeight: '700',
    fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.15s',
  },
  secondaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.6rem 1.25rem', borderRadius: '10px', border: '1.5px solid #E9EBEF',
    background: 'white', color: '#5C636E', fontWeight: '600',
    fontSize: '0.875rem', cursor: 'pointer',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none',
    background: '#DC2626', color: 'white', fontWeight: '700',
    fontSize: '0.875rem', cursor: 'pointer',
  },
  successBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    padding: '0.6rem 1.25rem', borderRadius: '10px', border: 'none',
    background: '#10B981', color: 'white', fontWeight: '700',
    fontSize: '0.875rem', cursor: 'pointer',
  },
  statCard: {
    background: '#FFFFFF', borderRadius: '12px', padding: '1rem',
    textAlign: 'center',
  },
  statValue: { fontSize: '1.5rem', fontWeight: '800', color: '#1E2229' },
  statLabel: { fontSize: '0.75rem', color: '#9AA0A8', marginTop: '0.25rem' },
  toast: (type) => ({
    padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '1rem',
    fontSize: '0.875rem', fontWeight: '500',
    background: type === 'error' ? '#fef2f2' : '#ECFDF5',
    color: type === 'error' ? '#dc2626' : '#10B981',
  }),
};

// ─── Component ───────────────────────────────────────────────
export default function SupplierDetailPage({ supplierId, onBack, onEditOwner }) {
  // Rôles de l'admin connecté → certaines actions sont réservées (Ops en lecture seule)
  const _roles = (() => { try { return JSON.parse(localStorage.getItem('currentAdmin') || '{}').roles; } catch { return undefined; } })();
  const _isSuper = !Array.isArray(_roles) || _roles.includes('superadmin'); // session legacy (sans roles) = superadmin
  const canManage = _isSuper || _roles.includes('validation');  // cycle de vie fournisseur (valider/suspendre/refuser/éditer)
  const canFinance = _isSuper || _roles.includes('finance');    // solde & virements
  const [supplier, setSupplier] = useState(null);
  const [owner, setOwner] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Recherche de lieu — même source que les apps natives : proxy Google Places
  // du backend en PRIMAIRE, repli OpenStreetMap (Nominatim) si Google ne renvoie
  // rien. Sert à corriger la position GPS d'un fournisseur mal placé.
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState([]);
  const [placeSearching, setPlaceSearching] = useState(false);
  const [placeStatus, setPlaceStatus] = useState(null); // null | 'empty' | message d'erreur
  const [placeHint, setPlaceHint] = useState(null); // { address } du lieu choisi
  const placeTimer = useRef(null);
  const placeSeq = useRef(0); // ignore les réponses d'une frappe dépassée

  const runPlaceSearch = async (raw) => {
    const q = String(raw || '').trim();
    if (q.length < 2) { setPlaceResults([]); setPlaceStatus(null); return; }
    const seq = ++placeSeq.current;
    setPlaceSearching(true);
    setPlaceStatus(null);
    // Biais géographique : position du formulaire, sinon celle du fournisseur, sinon Bukavu.
    const bLat = parseFloat(form.lat) || supplier?.location?.lat || -2.5083;
    const bLng = parseFloat(form.lng) || supplier?.location?.lng || 28.8608;
    let results = [];
    let errMsg = null;
    // 1) PRIMAIRE — proxy Google Places (apiService.get de l'admin n'envoie PAS
    //    d'objet de paramètres : la query string doit être dans l'URL).
    try {
      const qs = new URLSearchParams({ q, lat: String(bLat), lng: String(bLng) });
      const res = await apiService.get(`/places/autocomplete?${qs.toString()}`, { userType: 'admin' });
      results = (res?.results || []).slice(0, 8);
    } catch (e) { errMsg = e?.message || 'Erreur réseau'; }
    // 2) REPLI — OpenStreetMap, borné à 4 s (comme les apps natives).
    if (results.length === 0) {
      try {
        const vb = `${(bLng - 0.2).toFixed(4)},${(bLat - 0.2).toFixed(4)},${(bLng + 0.2).toFixed(4)},${(bLat + 0.2).toFixed(4)}`;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 4000);
        const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=6&viewbox=${vb}&bounded=0&accept-language=fr&countrycodes=cd`, { signal: ctrl.signal });
        clearTimeout(timer);
        if (r.ok) {
          const data = await r.json();
          results = (Array.isArray(data) ? data : []).map(it => ({
            placeId: `osm_${it.place_id}`,
            mainText: String(it.display_name || '').split(',')[0].trim(),
            secondaryText: String(it.display_name || '').split(',').slice(1, 3).join(',').trim(),
            description: it.display_name,
            coords: { latitude: parseFloat(it.lat), longitude: parseFloat(it.lon) },
          }));
        }
      } catch (_) { /* repli best-effort */ }
    }
    if (seq !== placeSeq.current) return; // une frappe plus récente a pris la main
    setPlaceResults(results);
    setPlaceSearching(false);
    setPlaceStatus(results.length ? null : (errMsg ? `Recherche impossible : ${errMsg}` : 'empty'));
  };

  const searchPlaces = (q) => {
    setPlaceQuery(q);
    setPlaceHint(null);
    clearTimeout(placeTimer.current);
    if (!q || q.trim().length < 2) { setPlaceResults([]); setPlaceStatus(null); setPlaceSearching(false); return; }
    placeTimer.current = setTimeout(() => runPlaceSearch(q), 350);
  };

  const pickPlace = async (p) => {
    setPlaceResults([]);
    setPlaceStatus(null);
    setPlaceQuery(p.description || p.mainText);
    // Résultat OpenStreetMap : coordonnées déjà connues.
    if (p.coords && Number.isFinite(p.coords.latitude)) {
      updateForm('lat', String(p.coords.latitude));
      updateForm('lng', String(p.coords.longitude));
      setPlaceHint({ address: p.description });
      return;
    }
    try {
      const res = await apiService.get(`/places/details?place_id=${encodeURIComponent(p.placeId)}`, { userType: 'admin' });
      if (res?.success && res.location) {
        updateForm('lat', String(res.location.latitude));
        updateForm('lng', String(res.location.longitude));
        setPlaceHint({ address: res.location.address || p.description });
      } else {
        setError('Impossible de résoudre ce lieu en coordonnées.');
      }
    } catch (e) { setError('Impossible de résoudre ce lieu en coordonnées.'); }
  };

  // Collage direct de « lat, lng » (copié depuis Google Maps) dans le champ latitude.
  const onLatChange = (v) => {
    const m = String(v).match(/^\s*(-?\d{1,2}(?:[.,]\d+)?)\s*[,;]\s*(-?\d{1,3}(?:[.,]\d+)?)\s*$/);
    if (m) {
      updateForm('lat', m[1].replace(',', '.'));
      updateForm('lng', m[2].replace(',', '.'));
    } else {
      updateForm('lat', v);
    }
  };
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Photo de profil (logo) et photo de couverture modifiables par l'admin — aide aux commerces
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);

  const onImagePicked = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-choisir le même fichier
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Choisissez une image (JPG, PNG…).'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image trop lourde (10 Mo maximum).'); return; }
    const setBusy = kind === 'logo' ? setLogoUploading : setCoverUploading;
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append(kind, file);
      const res = await apiService.upload(`/admin/delivery/suppliers/${supplier.supplier_id}/${kind}`, fd, { userType: 'admin' });
      setSupplier(prev => ({
        ...prev,
        logo: res.logo, logo_thumb: res.logo_thumb,
        cover_image: res.cover_image, cover_image_thumb: res.cover_image_thumb,
      }));
      setSuccess(kind === 'logo' ? 'Photo du commerce mise à jour ✅' : 'Photo de couverture mise à jour ✅');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Envoi de la photo impossible');
    } finally {
      setBusy(false);
    }
  };
  const onLogoPicked = onImagePicked('logo');
  const onCoverPicked = onImagePicked('cover');
  const [form, setForm] = useState({});

  // ── Suppression du fournisseur (superadmin) ──
  const [showDelete, setShowDelete] = useState(false);
  const [deleteOwner, setDeleteOwner] = useState(false);
  const [paymentsChoice, setPaymentsChoice] = useState('anonymize'); // paiements du propriétaire : garder anonymisés ou effacer (2026-11-02)
  const [ackDelete, setAckDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDelete = async () => {
    setDeleting(true); setDeleteError('');
    try {
      const res = await apiService.delete(`/admin/delivery/suppliers/${supplierId}?delete_owner=${deleteOwner}${deleteOwner ? `&payments=${paymentsChoice}` : ''}`, { userType: 'admin' });
      if (res?.success) { onBack && onBack(); }
      else setDeleteError(res?.error || 'Échec de la suppression');
    } catch (e) {
      setDeleteError(e?.error || e?.data?.error || e?.message || 'Échec de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // ── Balance state ──
  const [balanceData, setBalanceData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [showBalanceSection, setShowBalanceSection] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ amount: '', direction: 'credit', note: '' });
  const [payoutForm, setPayoutForm] = useState({ amount: '', note: '' });
  const [balanceActionLoading, setBalanceActionLoading] = useState(false);

  const fetchSupplier = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get(`/admin/delivery/suppliers/${supplierId}`, { userType: 'admin' });
      if (res.success) {
        setSupplier(res.supplier);
        setOwner(res.owner);
        setProductCount(res.product_count || 0);
        setRecentOrders(res.recent_orders || []);
        setForm({
          business_name: res.supplier.business_name || '',
          business_type: res.supplier.business_type || 'other',
          description: res.supplier.description || '',
          phone: res.supplier.phone || '',
          email: res.supplier.email || '',
          rccm: res.supplier.rccm || '',
          pharmacy_license_number: res.supplier.pharmacy_license_number || '',
          address: res.supplier.address || '',
          lat: res.supplier.location?.lat != null ? String(res.supplier.location.lat) : '',
          lng: res.supplier.location?.lng != null ? String(res.supplier.location.lng) : '',
          preparation_time: res.supplier.preparation_time || 30,
          minimum_order: res.supplier.minimum_order || 0,
          commission_rate: res.supplier.commission_rate || 0.05,
          status: res.supplier.status || 'pending',
          whatsapp_alerts_enabled: !!res.supplier.whatsapp_alerts_enabled,
          whatsapp_alert_phone: res.supplier.whatsapp_alert_phone || '',
        });
      }
    } catch (err) {
      setError(err.message || 'Impossible de charger le fournisseur');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    if (!supplier?.supplier_id) return;
    setBalanceLoading(true);
    try {
      const [balRes, txRes] = await Promise.all([
        apiService.get(`/balance/supplier/${supplier.supplier_id}`, { userType: 'admin' }),
        apiService.get(`/balance/supplier/${supplier.supplier_id}/transactions`, { userType: 'admin', period: 'all', limit: 20 }),
      ]);
      if (balRes.success) setBalanceData(balRes.balance);
      if (txRes.success) setTransactions(txRes.transactions || []);
    } catch (err) {
      console.error('Balance fetch error:', err);
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => { fetchSupplier(); }, [supplierId]);
  useEffect(() => { if (supplier?.supplier_id) fetchBalance(); }, [supplier?.supplier_id]);

  const handleAdjust = async () => {
    const amount = parseInt(adjustForm.amount, 10);
    if (!amount || amount <= 0) return setError('Montant invalide');
    setBalanceActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiService.post(`/balance/admin/supplier/${supplier.supplier_id}/adjust`, {
        amount, direction: adjustForm.direction, note: adjustForm.note || `Ajustement admin`
      }, { userType: 'admin' });
      if (res.success) {
        setSuccess(res.message || 'Ajustement effectué');
        setShowAdjustModal(false);
        setAdjustForm({ amount: '', direction: 'credit', note: '' });
        await fetchBalance();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Erreur ajustement');
    } finally {
      setBalanceActionLoading(false);
    }
  };

  const handlePayout = async () => {
    const amount = payoutForm.amount ? parseInt(payoutForm.amount, 10) : (balanceData?.current_balance || 0);
    if (amount <= 0) return setError('Montant de virement invalide');
    setBalanceActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiService.post(`/balance/admin/supplier/${supplier.supplier_id}/payout`, {
        amount, note: payoutForm.note || 'Virement admin'
      }, { userType: 'admin' });
      if (res.success) {
        setSuccess(res.message || 'Virement effectué');
        setShowPayoutModal(false);
        setPayoutForm({ amount: '', note: '' });
        await fetchBalance();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Erreur virement');
    } finally {
      setBalanceActionLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = { ...form };
      // Convert commission_rate from decimal input
      if (typeof payload.commission_rate === 'string') {
        payload.commission_rate = parseFloat(payload.commission_rate);
      }
      payload.preparation_time = parseInt(payload.preparation_time, 10) || 30;
      payload.minimum_order = parseInt(payload.minimum_order, 10) || 0;

      // Position géographique : envoyée seulement si les DEUX coordonnées sont
      // valides (l'app livreur et le calcul des frais en dépendent).
      const lat = parseFloat(payload.lat);
      const lng = parseFloat(payload.lng);
      delete payload.lat; delete payload.lng;
      const hasLat = String(form.lat).trim() !== '';
      const hasLng = String(form.lng).trim() !== '';
      if (hasLat || hasLng) {
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
          setSaving(false);
          return setError('Coordonnées invalides : latitude entre -90 et 90, longitude entre -180 et 180 (les deux requises).');
        }
        payload.location = { lat, lng };
      }

      const res = await apiService.put(`/admin/delivery/suppliers/${supplierId}`, payload, { userType: 'admin' });
      if (res.success) {
        setSuccess('Fournisseur mis à jour ✅');
        setEditing(false);
        await fetchSupplier();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await apiService.put(`/admin/delivery/suppliers/${supplierId}`, { status: newStatus }, { userType: 'admin' });
      if (res.success) {
        setSuccess(`Statut changé : ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
        await fetchSupplier();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du changement de statut');
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <span style={{ fontSize: '1.1rem', color: '#9AA0A8' }}>⏳ Chargement...</span>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div style={styles.container}>
        <button style={styles.backBtn} onClick={onBack}><ArrowLeft size={16} /> Retour</button>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9AA0A8' }}>
          <p style={{ fontSize: '2rem' }}>❌</p>
          <p>Fournisseur non trouvé</p>
        </div>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[supplier.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;
  const businessLabel = BUSINESS_TYPES.find(b => b.value === supplier.business_type)?.label || supplier.business_type;

  return (
    <div style={styles.container}>
      {/* Back */}
      <button style={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={16} /> Retour aux fournisseurs
      </button>

      {/* Toasts */}
      {error && <div style={styles.toast('error')}>{error}</div>}
      {success && <div style={styles.toast('success')}>{success}</div>}

      {/* Photo de couverture : bannière de la page boutique côté client (16:9 conseillé) */}
      <div
        onClick={() => !coverUploading && coverInputRef.current?.click()}
        title={supplier.cover_image ? 'Changer la photo de couverture' : 'Ajouter une photo de couverture'}
        style={{ ...styles.coverBand, cursor: coverUploading ? 'wait' : 'pointer', opacity: coverUploading ? 0.5 : 1 }}
      >
        {supplier.cover_image ? (
          <Img src={supplier.cover_image} alt="" style={styles.coverImg} />
        ) : (
          <div style={styles.coverEmpty}>
            <Camera size={18} /> Ajouter une photo de couverture (bannière de la page du commerce dans l'app client)
          </div>
        )}
        <span style={styles.coverBtn}>
          {coverUploading ? <RefreshCw size={12} /> : <Camera size={13} />}
          {supplier.cover_image ? 'Changer la couverture' : 'Ajouter une couverture'}
        </span>
        <input ref={coverInputRef} type="file" accept="image/*" onChange={onCoverPicked} style={{ display: 'none' }} />
      </div>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div
            onClick={() => !logoUploading && logoInputRef.current?.click()}
            title="Changer la photo du commerce"
            style={{ position: 'relative', cursor: logoUploading ? 'wait' : 'pointer', flexShrink: 0 }}
          >
            {supplier.logo ? (
              <Img src={supplier.logo} thumb={supplier.logo_thumb} alt="" style={{ ...styles.avatar, background: 'none', opacity: logoUploading ? 0.4 : 1 }} />
            ) : (
              <div style={{ ...styles.avatar, opacity: logoUploading ? 0.4 : 1 }}>
                {supplier.business_name?.charAt(0) || '?'}
              </div>
            )}
            <span style={{
              position: 'absolute', right: -2, bottom: -2, width: 26, height: 26, borderRadius: '50%',
              background: '#F54119', color: '#fff', border: '2px solid #fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {logoUploading ? <RefreshCw size={12} /> : <Camera size={13} />}
            </span>
            <input ref={logoInputRef} type="file" accept="image/*" onChange={onLogoPicked} style={{ display: 'none' }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '800', color: '#1E2229' }}>
              {supplier.business_name}
            </h2>
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.875rem', color: '#5C636E' }}>
              {businessLabel} · {supplier.supplier_id}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              <span style={styles.badge(statusCfg.bg, statusCfg.color)}>
                <StatusIcon size={13} /> {statusCfg.label}
              </span>
              {supplier.is_open && (
                <span style={styles.badge('#ECFDF5', '#10B981')}>
                  <Activity size={13} /> Ouvert
                </span>
              )}
              {supplier.public_id && (
                <span style={styles.badge('#FEE9E2', '#F54119')}>
                  {supplier.public_id}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={styles.headerRight}>
          {/* Page publique du commerce sur le site client (lien direct mbiyo.app/b/<id>), nouvel onglet */}
          <a
            href={`https://mbiyo.app/b/${encodeURIComponent(supplier.supplier_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.secondaryBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title={supplier.status === 'approved' ? 'Ouvrir la page du business sur mbiyo.app' : 'Le commerce n\'est visible des clients qu\'une fois approuvé'}
          >
            <ExternalLink size={15} /> Voir le business
          </a>
          {!editing ? (
            canManage && (
              <button style={styles.primaryBtn} onClick={() => setEditing(true)}>
                <Edit3 size={15} /> Modifier
              </button>
            )
          ) : (
            <>
              <button style={styles.secondaryBtn} onClick={() => { setEditing(false); fetchSupplier(); }}>
                Annuler
              </button>
              <button style={{ ...styles.primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
                <Save size={15} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ ...styles.grid3, marginBottom: '1rem' }}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{supplier.total_orders || 0}</div>
          <div style={styles.statLabel}>Commandes</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{(supplier.total_revenue || 0).toLocaleString()} CDF</div>
          <div style={styles.statLabel}>Revenu total</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{productCount}</div>
          <div style={styles.statLabel}>Produits</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>⭐ {supplier.rating?.toFixed(1) || '—'}</div>
          <div style={styles.statLabel}>{supplier.rating_count || 0} avis</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{Math.round((supplier.commission_rate || 0.05) * 100)}%</div>
          <div style={styles.statLabel}>Commission</div>
        </div>
      </div>

      {/* Menu : associer un modèle de boutique (rayons, produits, prix, options) */}
      {supplier.business_type !== 'event_organizer' && (
        <AssignMenuPanel
          supplier={supplier}
          productCount={productCount}
          canEdit={canManage || (Array.isArray(_roles) && _roles.includes('ops_delivery'))}
          onDone={fetchSupplier}
        />
      )}

      {/* Informations */}
      <div style={styles.card}>
        <div style={styles.cardTitle}><Store size={18} /> Informations générales</div>
        {editing ? (
          <div style={styles.grid2}>
            <div style={styles.field}>
              <div style={styles.label}>Nom commercial</div>
              <input style={styles.input} value={form.business_name} onChange={e => updateForm('business_name', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Type d'activité</div>
              <select style={styles.select} value={form.business_type} onChange={e => updateForm('business_type', e.target.value)}>
                {BUSINESS_TYPES.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
              </select>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Téléphone</div>
              <input style={styles.input} value={form.phone} onChange={e => updateForm('phone', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Email</div>
              <input style={styles.input} value={form.email} onChange={e => updateForm('email', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>RCCM</div>
              <input style={styles.input} value={form.rccm} onChange={e => updateForm('rccm', e.target.value)} placeholder="Registre commercial" />
            </div>
            {form.business_type === 'pharmacy' && (
              <div style={styles.field}>
                <div style={styles.label}>N° d'autorisation (pharmacie) *</div>
                <input style={styles.input} value={form.pharmacy_license_number || ''} onChange={e => updateForm('pharmacy_license_number', e.target.value)} placeholder="Autorisation d'exploitation / Ordre des pharmaciens" />
              </div>
            )}
            <div style={styles.field}>
              <div style={styles.label}>Statut</div>
              <select style={styles.select} value={form.status} onChange={e => updateForm('status', e.target.value)}>
                <option value="pending">En attente</option>
                <option value="approved">Validé</option>
                <option value="rejected">Refusé</option>
                <option value="suspended">Suspendu</option>
              </select>
            </div>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
              <div style={styles.label}>Description</div>
              <textarea style={styles.textarea} value={form.description} onChange={e => updateForm('description', e.target.value)} />
            </div>
          </div>
        ) : (
          <div style={styles.grid2}>
            <div style={styles.field}>
              <div style={styles.label}>Nom commercial</div>
              <div style={styles.value}>{supplier.business_name}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Type d'activité</div>
              <div style={styles.value}>{businessLabel}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Téléphone</div>
              <div style={styles.value}><Phone size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{supplier.phone || '—'}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Email</div>
              <div style={styles.value}><Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{supplier.email || '—'}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>RCCM</div>
              <div style={styles.value}>{supplier.rccm || '—'}</div>
            </div>
            {supplier.business_type === 'pharmacy' && (
              <div style={styles.field}>
                <div style={styles.label}>N° d'autorisation (pharmacie)</div>
                <div style={{ ...styles.value, color: supplier.pharmacy_license_number ? undefined : '#DC2626', fontWeight: supplier.pharmacy_license_number ? undefined : 700 }}>
                  {supplier.pharmacy_license_number || '⚠ Manquant — requis pour valider'}
                  {supplier.on_duty ? '  ·  🌙 De garde' : ''}
                </div>
              </div>
            )}
            <div style={styles.field}>
              <div style={styles.label}>Créé le</div>
              <div style={styles.value}>{supplier.created_at ? new Date(supplier.created_at).toLocaleDateString('fr-FR') : '—'}</div>
            </div>
            {supplier.description && (
              <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                <div style={styles.label}>Description</div>
                <div style={styles.value}>{supplier.description}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Localisation */}
      <div style={styles.card}>
        <div style={styles.cardTitle}><MapPin size={18} /> Localisation</div>
        {editing ? (
          <>
            <div style={styles.field}>
              <div style={styles.label}>Adresse</div>
              <input style={styles.input} value={form.address} onChange={e => updateForm('address', e.target.value)} />
            </div>

            {/* Position GPS — corrige un fournisseur mal placé depuis son app */}
            <div style={{ ...styles.field, marginTop: 12 }}>
              <div style={styles.label}>📍 Position géographique — rechercher un lieu</div>
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    value={placeQuery}
                    onChange={e => searchPlaces(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(placeTimer.current); runPlaceSearch(placeQuery); } }}
                    placeholder="Ex. Pharmacie du Peuple, Av. Lumumba, Kinshasa…"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => { clearTimeout(placeTimer.current); runPlaceSearch(placeQuery); }}
                    style={{ border: 'none', background: '#C9350F', color: '#fff', borderRadius: 8, padding: '0 14px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {placeSearching ? 'Recherche…' : 'Rechercher'}
                  </button>
                </div>
                {placeResults.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                    background: '#fff', border: '1px solid #E9EBEF', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.12)', maxHeight: 240, overflowY: 'auto',
                  }}>
                    {placeResults.map(p => (
                      <div
                        key={p.placeId}
                        onClick={() => pickPlace(p)}
                        style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid #F4F5F7' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#FFFFFF'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                      >
                        <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E2229' }}>{p.mainText}</div>
                        {p.secondaryText ? <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>{p.secondaryText}</div> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {placeStatus && !placeSearching && (
                <div style={{ marginTop: 6, fontSize: '0.8rem', color: placeStatus === 'empty' ? '#C9350F' : '#B91C1C' }}>
                  {placeStatus === 'empty'
                    ? 'Aucun lieu trouvé — essayez un autre nom (quartier, avenue, repère) ou saisissez les coordonnées ci-dessous.'
                    : `⚠ ${placeStatus}`}
                </div>
              )}
              {placeHint && (
                <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#166534', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  ✓ Coordonnées récupérées{placeHint.address ? ` — ${placeHint.address}` : ''}
                  {placeHint.address && (
                    <button
                      type="button"
                      onClick={() => updateForm('address', placeHint.address)}
                      style={{ border: '1px solid #10B981', background: '#ECFDF5', color: '#166534', borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', cursor: 'pointer' }}
                    >
                      Utiliser comme adresse
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ ...styles.field, flex: 1, minWidth: 160 }}>
                <div style={styles.label}>Latitude (ou coller « lat, lng »)</div>
                <input style={styles.input} value={form.lat} onChange={e => onLatChange(e.target.value)} placeholder="-2.504400" />
              </div>
              <div style={{ ...styles.field, flex: 1, minWidth: 160 }}>
                <div style={styles.label}>Longitude</div>
                <input style={styles.input} value={form.lng} onChange={e => updateForm('lng', e.target.value)} placeholder="28.861100" />
              </div>
              {parseFloat(form.lat) && parseFloat(form.lng) ? (
                <a
                  href={`https://www.google.com/maps?q=${parseFloat(form.lat)},${parseFloat(form.lng)}`}
                  target="_blank" rel="noreferrer"
                  style={{ fontSize: '0.82rem', color: '#C9350F', fontWeight: 600, paddingBottom: 10, whiteSpace: 'nowrap' }}
                >
                  🗺 Vérifier sur Google Maps
                </a>
              ) : null}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9AA0A8', marginTop: 4 }}>
              Cette position sert au calcul des frais de livraison et guide les livreurs vers la boutique. Vérifiez-la sur la carte avant d'enregistrer.
            </div>
          </>
        ) : (
          <>
            <div style={styles.field}>
              <div style={styles.label}>Adresse</div>
              <div style={styles.value}>{supplier.address || '—'}</div>
            </div>
            {supplier.address_details?.reference && (
              <div style={styles.field}>
                <div style={styles.label}>Référence</div>
                <div style={styles.value}>{supplier.address_details.reference}</div>
              </div>
            )}
            {supplier.location?.lat && (
              <div style={styles.field}>
                <div style={styles.label}>Coordonnées</div>
                <div style={styles.value}>
                  {supplier.location.lat.toFixed(6)}, {supplier.location.lng.toFixed(6)}
                  {'  '}
                  <a
                    href={`https://www.google.com/maps?q=${supplier.location.lat},${supplier.location.lng}`}
                    target="_blank" rel="noreferrer"
                    style={{ fontSize: '0.8rem', color: '#C9350F', fontWeight: 600, marginLeft: 8 }}
                  >
                    🗺 Voir sur la carte
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Paramètres */}
      <div style={styles.card}>
        <div style={styles.cardTitle}><Briefcase size={18} /> Paramètres</div>
        {editing ? (
          <div style={styles.grid3}>
            <div style={styles.field}>
              <div style={styles.label}>Temps de préparation (min)</div>
              <input style={styles.input} type="number" value={form.preparation_time} onChange={e => updateForm('preparation_time', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Commande minimum (CDF)</div>
              <input style={styles.input} type="number" value={form.minimum_order} onChange={e => updateForm('minimum_order', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Commission (décimal, ex: 0.05 = 5%)</div>
              <input style={styles.input} type="number" step="0.01" min="0" max="1" value={form.commission_rate} onChange={e => updateForm('commission_rate', e.target.value)} />
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Alertes WhatsApp des commandes</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 6 }}>
                <input type="checkbox" checked={!!form.whatsapp_alerts_enabled} onChange={e => updateForm('whatsapp_alerts_enabled', e.target.checked)} />
                Activées (Accepter / Refuser, En préparation, Prête, code de retrait)
              </label>
              <input style={styles.input} value={form.whatsapp_alert_phone} onChange={e => updateForm('whatsapp_alert_phone', e.target.value)} placeholder={`Vide = numéro du propriétaire${owner?.phone ? ` (${owner.phone})` : ''}`} />
            </div>
          </div>
        ) : (
          <div style={styles.grid3}>
            <div style={styles.field}>
              <div style={styles.label}>Temps de préparation</div>
              <div style={styles.value}>{supplier.preparation_time || 30} min</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Commande minimum</div>
              <div style={styles.value}>{(supplier.minimum_order || 0).toLocaleString()} CDF</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Commission</div>
              <div style={styles.value}>{Math.round((supplier.commission_rate || 0.05) * 100)}%</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Alertes WhatsApp des commandes</div>
              <div style={styles.value}>
                {supplier.whatsapp_alerts_enabled
                  ? <span style={{ color: '#047857' }}>Activées · {supplier.whatsapp_alert_phone || owner?.phone || 'numéro du propriétaire'}</span>
                  : <span style={{ color: '#6B7280' }}>Désactivées</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Propriétaire */}
      {owner && (
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={styles.cardTitle}><User size={18} /> Propriétaire</div>
            {onEditOwner && (owner.user_id) && (
              <button
                onClick={() => onEditOwner(owner.user_id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#C9350F', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 13px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                <Edit3 size={14} /> Modifier le propriétaire
              </button>
            )}
          </div>
          <div style={styles.grid2}>
            <div style={styles.field}>
              <div style={styles.label}>Nom</div>
              <div style={styles.value}>{owner.name}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Téléphone</div>
              <div style={styles.value}>{owner.phone || '—'}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>Email</div>
              <div style={styles.value}>{owner.email || '—'}</div>
            </div>
            <div style={styles.field}>
              <div style={styles.label}>ID Public</div>
              <div style={styles.value}>{owner.public_id || owner.user_id}</div>
            </div>
          </div>
        </div>
      )}

      {/* Actions rapides (cycle de vie fournisseur — réservé à la Validation) */}
      {!editing && canManage && (
        <div style={styles.card}>
          <div style={styles.cardTitle}><Activity size={18} /> Actions rapides</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {supplier.status !== 'approved' && (
              <button style={styles.successBtn} onClick={() => handleStatusChange('approved')} disabled={saving}>
                <CheckCircle size={15} /> Valider
              </button>
            )}
            {supplier.status !== 'suspended' && supplier.status === 'approved' && (
              <button style={{ ...styles.primaryBtn, background: '#ea580c' }} onClick={() => handleStatusChange('suspended')} disabled={saving}>
                <AlertTriangle size={15} /> Suspendre
              </button>
            )}
            {supplier.status === 'suspended' && (
              <button style={styles.successBtn} onClick={() => handleStatusChange('approved')} disabled={saving}>
                <CheckCircle size={15} /> Réactiver
              </button>
            )}
            {supplier.status !== 'rejected' && supplier.status !== 'approved' && (
              <button style={styles.dangerBtn} onClick={() => handleStatusChange('rejected')} disabled={saving}>
                <XCircle size={15} /> Refuser
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── BALANCE MANAGEMENT ─── */}
      <div style={styles.card}>
        <div style={{ ...styles.cardTitle, cursor: 'pointer', justifyContent: 'space-between' }}
          onClick={() => setShowBalanceSection(!showBalanceSection)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Wallet size={18} /> Gestion du solde
          </span>
          {showBalanceSection ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>

        {showBalanceSection && (
          <>
            {/* Balance overview */}
            {balanceLoading ? (
              <p style={{ color: '#9AA0A8', textAlign: 'center', padding: '1rem' }}>Chargement...</p>
            ) : balanceData ? (
              <>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <div style={{
                    flex: '1', minWidth: '200px', padding: '1.25rem', borderRadius: '14px',
                    background: (balanceData.current_balance || 0) >= 0 ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #DC2626, #dc2626)',
                    color: 'white', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '0.8rem', opacity: 0.85, marginBottom: '0.25rem' }}>Solde actuel</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: '900' }}>
                      {(balanceData.current_balance || 0).toLocaleString()} CDF
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                      {(balanceData.current_balance || 0) >= 0 ? 'Plateforme doit au fournisseur' : 'Fournisseur doit à la plateforme'}
                    </div>
                  </div>
                </div>

                <div style={{ ...styles.grid3, marginBottom: '1rem' }}>
                  <div style={styles.statCard}>
                    <div style={styles.statValue}>{balanceData.total_sales_all_time || 0}</div>
                    <div style={styles.statLabel}>Ventes totales</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statValue}>{(balanceData.total_revenue_all_time || 0).toLocaleString()}</div>
                    <div style={styles.statLabel}>Revenu brut (CDF)</div>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statValue}>{(balanceData.total_commission_all_time || 0).toLocaleString()}</div>
                    <div style={styles.statLabel}>Commission totale (CDF)</div>
                  </div>
                </div>

                {/* Action buttons (solde & virements — réservé à la Finance) */}
                {canFinance && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  <button style={{ ...styles.primaryBtn, background: '#FF7800' }} onClick={() => setShowAdjustModal(true)}>
                    <Edit3 size={15} /> Ajustement
                  </button>
                  {(balanceData.current_balance || 0) > 0 && (
                    <button style={styles.successBtn} onClick={() => {
                      setPayoutForm({ amount: String(balanceData.current_balance), note: '' });
                      setShowPayoutModal(true);
                    }}>
                      <CreditCard size={15} /> Virer {balanceData.current_balance.toLocaleString()} CDF
                    </button>
                  )}
                  <button style={styles.secondaryBtn} onClick={fetchBalance}>
                    <RefreshCw size={15} /> Actualiser
                  </button>
                </div>
                )}

                {/* Adjust modal */}
                {showAdjustModal && (
                  <div style={{ background: '#FFFFFF', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', border: '2px solid #E9EBEF' }}>
                    <h4 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '700', color: '#1E2229' }}>Ajustement du solde</h4>
                    <div style={styles.grid2}>
                      <div style={styles.field}>
                        <div style={styles.label}>Montant (CDF)</div>
                        <input style={styles.input} type="number" min="1" placeholder="Ex: 5000"
                          value={adjustForm.amount} onChange={e => setAdjustForm(p => ({ ...p, amount: e.target.value }))} />
                      </div>
                      <div style={styles.field}>
                        <div style={styles.label}>Direction</div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => setAdjustForm(p => ({ ...p, direction: 'credit' }))}
                            style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem',
                              background: adjustForm.direction === 'credit' ? '#ECFDF5' : '#F4F5F7',
                              color: adjustForm.direction === 'credit' ? '#10B981' : '#9AA0A8',
                            }}>
                            <Plus size={14} style={{ verticalAlign: 'middle' }} /> Crédit
                          </button>
                          <button onClick={() => setAdjustForm(p => ({ ...p, direction: 'debit' }))}
                            style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem',
                              background: adjustForm.direction === 'debit' ? '#fef2f2' : '#F4F5F7',
                              color: adjustForm.direction === 'debit' ? '#DC2626' : '#9AA0A8',
                            }}>
                            <Minus size={14} style={{ verticalAlign: 'middle' }} /> Débit
                          </button>
                        </div>
                      </div>
                      <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                        <div style={styles.label}>Note (optionnel)</div>
                        <input style={styles.input} placeholder="Raison de l'ajustement"
                          value={adjustForm.note} onChange={e => setAdjustForm(p => ({ ...p, note: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button style={styles.secondaryBtn} onClick={() => setShowAdjustModal(false)}>Annuler</button>
                      <button style={{ ...styles.primaryBtn, background: '#FF7800', opacity: balanceActionLoading ? 0.6 : 1 }}
                        onClick={handleAdjust} disabled={balanceActionLoading}>
                        {balanceActionLoading ? 'En cours...' : 'Confirmer'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Payout modal */}
                {showPayoutModal && (
                  <div style={{ background: '#ECFDF5', borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem', border: '2px solid #bbf7d0' }}>
                    <h4 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: '700', color: '#1E2229' }}>Virement au fournisseur</h4>
                    <div style={styles.grid2}>
                      <div style={styles.field}>
                        <div style={styles.label}>Montant (CDF)</div>
                        <input style={styles.input} type="number" min="1"
                          value={payoutForm.amount} onChange={e => setPayoutForm(p => ({ ...p, amount: e.target.value }))} />
                      </div>
                      <div style={styles.field}>
                        <div style={styles.label}>Note (optionnel)</div>
                        <input style={styles.input} placeholder="Ex: virement Mobile Money"
                          value={payoutForm.note} onChange={e => setPayoutForm(p => ({ ...p, note: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                      <button style={styles.secondaryBtn} onClick={() => setShowPayoutModal(false)}>Annuler</button>
                      <button style={{ ...styles.successBtn, opacity: balanceActionLoading ? 0.6 : 1 }}
                        onClick={handlePayout} disabled={balanceActionLoading}>
                        {balanceActionLoading ? 'En cours...' : 'Confirmer le virement'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Transaction history */}
                {transactions.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1E2229', marginBottom: '0.5rem' }}>Historique transactions</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #F4F5F7' }}>
                            <th style={{ textAlign: 'left', padding: '0.4rem', color: '#9AA0A8', fontWeight: '600' }}>Type</th>
                            <th style={{ textAlign: 'right', padding: '0.4rem', color: '#9AA0A8', fontWeight: '600' }}>Montant</th>
                            <th style={{ textAlign: 'right', padding: '0.4rem', color: '#9AA0A8', fontWeight: '600' }}>Solde après</th>
                            <th style={{ textAlign: 'left', padding: '0.4rem', color: '#9AA0A8', fontWeight: '600' }}>Note</th>
                            <th style={{ textAlign: 'right', padding: '0.4rem', color: '#9AA0A8', fontWeight: '600' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((txn, i) => {
                            const typeLabels = {
                              sale_credit: { label: 'Vente', color: '#10B981' },
                              commission_debit: { label: 'Commission', color: '#FFB900' },
                              payout: { label: 'Virement', color: '#FF7800' },
                              admin_adjustment: { label: 'Ajustement', color: '#FF7800' },
                              refund_debit: { label: 'Remboursement', color: '#DC2626' },
                              ordinary_ride_driver_share: { label: 'Part chauffeur affilié', color: '#10B981' },
                            };
                            const tl = typeLabels[txn.type] || { label: txn.type, color: '#9AA0A8' };
                            return (
                              <tr key={txn._id || i} style={{ borderBottom: '1px solid #FFFFFF' }}>
                                <td style={{ padding: '0.4rem' }}>
                                  <span style={{ padding: '0.15rem 0.5rem', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600', color: tl.color, background: tl.color + '15' }}>
                                    {tl.label}
                                  </span>
                                </td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '700',
                                  color: txn.direction === 'credit' ? '#10B981' : '#DC2626' }}>
                                  {txn.direction === 'credit' ? '+' : '-'}{(txn.amount || 0).toLocaleString()} CDF
                                </td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', color: '#5C636E', fontWeight: '500' }}>
                                  {(txn.balance_after ?? 0).toLocaleString()} CDF
                                </td>
                                <td style={{ padding: '0.4rem', color: '#9AA0A8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {txn.note || '—'}
                                </td>
                                <td style={{ padding: '0.4rem', textAlign: 'right', color: '#9AA0A8' }}>
                                  {txn.created_at ? new Date(txn.created_at).toLocaleDateString('fr-FR') : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#9AA0A8', textAlign: 'center', padding: '1rem' }}>Aucune donnée de solde disponible</p>
            )}
          </>
        )}
      </div>

      {/* Commandes récentes */}
      {recentOrders.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}><FileText size={18} /> Dernières commandes</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #F4F5F7' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: '#9AA0A8', fontWeight: '600' }}>N°</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', color: '#9AA0A8', fontWeight: '600' }}>Statut</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem', color: '#9AA0A8', fontWeight: '600' }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '0.5rem', color: '#9AA0A8', fontWeight: '600' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, i) => (
                  <tr key={order._id || i} style={{ borderBottom: '1px solid #FFFFFF' }}>
                    <td style={{ padding: '0.5rem', fontWeight: '600', color: '#1E2229' }}>
                      #{order.order_number || '—'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <span style={{
                        padding: '0.15rem 0.5rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '600',
                        background: order.status === 'delivered' ? '#ECFDF5' : order.status === 'cancelled' ? '#fef2f2' : '#fef9c3',
                        color: order.status === 'delivered' ? '#10B981' : order.status === 'cancelled' ? '#dc2626' : '#ca8a04',
                      }}>
                        {order.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: '600' }}>
                      {(order.total_amount || 0).toLocaleString()} CDF
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', color: '#9AA0A8' }}>
                      {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Zone dangereuse — suppression complète (superadmin) */}
      {_isSuper && !editing && (
        <div style={{ ...styles.card, border: '1px solid #fecaca' }}>
          <div style={{ ...styles.cardTitle, color: '#dc2626' }}><Trash2 size={18} /> Zone dangereuse</div>
          <p style={{ fontSize: '0.85rem', color: '#5C636E', margin: '6px 0 12px', lineHeight: 1.5 }}>
            Supprimer définitivement ce fournisseur et son catalogue (produits, services, hébergements, véhicules).
            L'historique (commandes, réservations, avis, transactions) est conservé.
          </p>
          <button onClick={() => { setShowDelete(true); setDeleteOwner(false); setAckDelete(false); setDeleteError(''); }}
            style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trash2 size={15} /> Supprimer le fournisseur
          </button>
        </div>
      )}

      {/* Modale de confirmation de suppression */}
      {showDelete && (
        <div onClick={() => !deleting && setShowDelete(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 'min(500px, 96vw)', padding: 22 }}>
            <h3 style={{ margin: '0 0 8px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}><AlertTriangle size={20} /> Suppression définitive</h3>
            <p style={{ fontSize: '0.9rem', color: '#2C313A', lineHeight: 1.5 }}>
              Supprimer « <strong>{supplier?.business_name}</strong> » ? Cette action est <strong>irréversible</strong>.
            </p>
            <ul style={{ fontSize: '0.82rem', color: '#5C636E', lineHeight: 1.6, margin: '8px 0', paddingLeft: 18 }}>
              <li>Supprimé : fiche fournisseur + catalogue (produits/services/hébergements/véhicules).</li>
              <li>Conservé : commandes, réservations, avis, transactions de solde, retraits.</li>
            </ul>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', color: '#2C313A', margin: '10px 0', padding: '10px', background: '#fef2f2', borderRadius: 8 }}>
              <input type="checkbox" checked={deleteOwner} onChange={e => setDeleteOwner(e.target.checked)} />
              <span>Supprimer aussi le <strong>compte propriétaire</strong>{owner ? ` (${owner.name})` : ''} — connexion + profil personnel.</span>
            </label>
            {deleteOwner && (
              <div style={{ fontSize: '0.85rem', color: '#2C313A', margin: '0 0 12px', padding: '10px', background: '#F4F5F7', borderRadius: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Paiements Mobile Money du propriétaire (pièces comptables) :</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <input type="radio" name="paymentsChoice" checked={paymentsChoice === 'anonymize'} onChange={() => setPaymentsChoice('anonymize')} />
                  <span><strong>Garder anonymisés</strong> — montant, date et statut conservés ; numéro et traces personnelles effacés (recommandé).</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" name="paymentsChoice" checked={paymentsChoice === 'delete'} onChange={() => setPaymentsChoice('delete')} />
                  <span><strong>Effacer</strong> définitivement les paiements.</span>
                </label>
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#2C313A', margin: '6px 0 14px' }}>
              <input type="checkbox" checked={ackDelete} onChange={e => setAckDelete(e.target.checked)} />
              <span>Je comprends que cette action est irréversible.</span>
            </label>
            {deleteError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: '0.85rem', fontWeight: 600 }}>{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowDelete(false)} disabled={deleting} style={{ background: '#E9EBEF', color: '#414852', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
              <button onClick={handleDelete} disabled={!ackDelete || deleting} style={{ background: (!ackDelete || deleting) ? '#fca5a5' : '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, cursor: (!ackDelete || deleting) ? 'not-allowed' : 'pointer' }}>
                {deleting ? 'Suppression…' : (deleteOwner ? 'Supprimer fournisseur + compte' : 'Supprimer le fournisseur')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
